import { inject, Injectable } from '@angular/core';
import { createCrudUrl, CrudRequestParams } from '../pagination/models/interfaces/crud';
import { Track } from '../../models/interfaces/track.interface';
import { tracksMock } from '../../mocks/interfaces/track.mock';
import { PaginationDto } from '../pagination/models/interfaces/pagination-dto';
import { Api } from '../api/api';
import { TrackPayload } from '../../types/payloads/track.payload';
import { isFakeBackendEnabled } from '../../utils/fake-backend.util';

@Injectable({
  providedIn: 'root',
})

export class TrackService {

  private readonly api = inject(Api);

  private readonly baseRoute = '/tracks';

  public async getManyMocked(params?: CrudRequestParams<Track>): Promise<PaginationDto<Track>> {
    const page = Math.max(1, params?.page ?? 1);
    const limit = Math.max(1, params?.limit ?? 10);
    const search = this.getSearchValue(params?.search);

    const filteredTracks = tracksMock.filter((track) => {
      if (!search) {
        return true;
      }

      return track.name.toLowerCase().includes(search)
        || (track.description || '').toLowerCase().includes(search);
    });

    const sortedTracks = [...filteredTracks].sort((left, right) => {
      const firstSort = params?.sort?.[0];

      if (!firstSort?.field) {
        return 0;
      }

      const field = firstSort.field as keyof Track;
      const direction = firstSort.order === 'ASC' ? 1 : -1;

      const leftValue = left[field] as string | number | Date | undefined;
      const rightValue = right[field] as string | number | Date | undefined;

      if (leftValue == null && rightValue == null) {
        return 0;
      }

      if (leftValue == null) {
        return -1 * direction;
      }

      if (rightValue == null) {
        return 1 * direction;
      }

      if (leftValue instanceof Date && rightValue instanceof Date) {
        return (leftValue.getTime() - rightValue.getTime()) * direction;
      }

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * direction;
      }

      return String(leftValue).localeCompare(String(rightValue)) * direction;
    });

    const total = sortedTracks.length;
    const pageCount = Math.max(1, Math.ceil(total / limit));
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * limit;
    const data = sortedTracks.slice(start, start + limit);

    return {
      data,
      page: safePage,
      pageCount,
      count: data.length,
      total,
    };
  }

  public async createMocked(payload: TrackPayload): Promise<Track> {
    if (!payload.name.trim()) {
      throw new Error('Nome da trilha e obrigatorio.');
    }

    const now = new Date();
    const newTrack: Track = {
      id: `track-${crypto.randomUUID()}`,
      tenantId: 'tenant-uuid',
      name: payload.name.trim(),
      coursesIds: payload.coursesIds ?? [],
      shortDescription: payload.shortDescription?.trim() || undefined,
      description: payload.description?.trim() || undefined,
      coverImageUrl: payload.coverImageUrl?.trim() || undefined,
      createdAt: now,
      updatedAt: now,
      isActive: true,
    };

    tracksMock.unshift(newTrack);

    return newTrack;
  }

  public async updateMocked(trackId: string, payload: TrackPayload): Promise<Track> {
    const targetIndex = tracksMock.findIndex(track => track.id === trackId);

    if (targetIndex < 0) {
      throw new Error('Trilha nao encontrada para edicao.');
    }

    const current = tracksMock[targetIndex];
    const updated: Track = {
      ...current,
      name: payload.name.trim() || current.name,
      coursesIds: payload.coursesIds ?? current.coursesIds,
      shortDescription: payload.shortDescription?.trim() || undefined,
      description: payload.description?.trim() || undefined,
      coverImageUrl: payload.coverImageUrl?.trim() || undefined,
      updatedAt: new Date(),
    };

    tracksMock[targetIndex] = updated;

    return updated;
  }

  public async deleteMocked(trackId: string): Promise<void> {
    const targetIndex = tracksMock.findIndex((track) => track.id === trackId);

    if (targetIndex < 0) {
      throw new Error('Trilha não encontrada.');
    }

    tracksMock.splice(targetIndex, 1);
  }

  public async setActiveMocked(trackId: string, isActive: boolean): Promise<Track> {
    const targetIndex = tracksMock.findIndex((track) => track.id === trackId);

    if (targetIndex < 0) {
      throw new Error('Trilha não encontrada.');
    }

    const updated: Track = {
      ...tracksMock[targetIndex],
      isActive,
      updatedAt: new Date(),
    };

    tracksMock[targetIndex] = updated;
    return updated;
  }

  // ─── Public API (fake backend ou integração real) ─────────────────────────

  public async getMany(crudParams?: CrudRequestParams<Track>): Promise<PaginationDto<Track>> {
    if (isFakeBackendEnabled()) {
      return this.getManyMocked(crudParams);
    }

    const url = createCrudUrl<Track>(this.baseRoute, crudParams);

    const response = await this.api.getAsync<PaginationDto<Track>>(url);

    if (!response) {
      return {
        data: [],
        page: 0,
        pageCount: 0,
        count: 0,
        total: 0,
      };
    }

    return response;
  }

  public async create(payload: TrackPayload): Promise<Track> {
    if (isFakeBackendEnabled()) {
      return this.createMocked(payload);
    }

    const response = await this.api.postAsync<Track>(this.baseRoute, payload);

    if (!response) {
      throw new Error('Falha ao criar a trilha.');
    }

    return response;
  }

  public async update(trackId: string, payload: TrackPayload): Promise<Track> {
    if (isFakeBackendEnabled()) {
      return this.updateMocked(trackId, payload);
    }

    const response = await this.api.putAsync<Track>(`${this.baseRoute}/${trackId}`, payload);

    if (!response) {
      throw new Error('Falha ao atualizar a trilha.');
    }

    return response;
  }

  public async delete(trackId: string): Promise<void> {
    if (isFakeBackendEnabled()) {
      return this.deleteMocked(trackId);
    }

    await this.api.deleteAsync(`${this.baseRoute}/${trackId}`);
  }

  public async setActive(trackId: string, isActive: boolean): Promise<Track> {
    if (isFakeBackendEnabled()) {
      return this.setActiveMocked(trackId, isActive);
    }

    const response = await this.api.patchAsync<Track>(
      `${this.baseRoute}/${trackId}/status`,
      { status: isActive },
    );

    if (!response) {
      throw new Error(isActive ? 'Falha ao ativar a trilha.' : 'Falha ao desativar a trilha.');
    }

    return response;
  }

  private getSearchValue(search?: CrudRequestParams<Track>['search']): string {
    if (!search) {
      return '';
    }

    return this.extractSearchFromCondition(search) || '';
  }

  private extractSearchFromCondition(condition: unknown): string | undefined {
    if (!condition || typeof condition !== 'object') {
      return undefined;
    }

    const record = condition as Record<string, unknown>;
    const fieldSearch = this.extractSearchFromField(record['name'])
      || this.extractSearchFromField(record['description']);

    if (fieldSearch) {
      return fieldSearch;
    }

    const orConditions = Array.isArray(record['$or']) ? record['$or'] as unknown[] : [];

    for (const nestedCondition of orConditions) {
      const nestedSearch = this.extractSearchFromCondition(nestedCondition);

      if (nestedSearch) {
        return nestedSearch;
      }
    }

    return undefined;
  }

  private extractSearchFromField(field: unknown): string | undefined {
    if (typeof field === 'string') {
      return field.toLowerCase().trim();
    }

    if (!field || typeof field !== 'object') {
      return undefined;
    }

    const operators = field as Record<string, unknown>;
    const value = operators['$contL'] ?? operators['$cont'];

    if (typeof value === 'string') {
      return value.toLowerCase().trim();
    }

    return undefined;
  }
}
